import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { helpdeskAPI } from '../services/api';
import './Profile.css';

function Profile() {
    const { client, updateClient } = useAuth();
    const { t, language } = useLanguage();
    
    const [formData, setFormData] = useState({
        name: client?.name || '',
        phone: client?.phone || '',
        company: client?.company || ''
    });
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
        setSuccess('');
    };

    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
        setError('');
        setSuccess('');
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            setLoading(true);
            const response = await helpdeskAPI.updateProfile(formData);
            if (response.data.success) {
                updateClient(response.data.client);
                setSuccess(t('profileUpdated'));
            }
        } catch (error) {
            setError(error.response?.data?.message || (language === 'ar' ? 'فشل تحديث الملف الشخصي' : 'Failed to update profile'));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError(language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
            return;
        }

        if (passwordData.newPassword.length < 6) {
            setError(language === 'ar' ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
            return;
        }

        try {
            setPasswordLoading(true);
            const response = await helpdeskAPI.changePassword({
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            if (response.data.success) {
                setSuccess(t('passwordChanged'));
                setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
            }
        } catch (error) {
            setError(error.response?.data?.message || (language === 'ar' ? 'فشل تغيير كلمة المرور' : 'Failed to change password'));
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <div className="profile-page">
            <div className="page-header">
                <h1>{language === 'ar' ? 'ملفي الشخصي' : 'My Profile'}</h1>
                <p>{language === 'ar' ? 'إدارة إعدادات حسابك' : 'Manage your account settings'}</p>
            </div>

            {success && <div className="alert alert-success">✓ {success}</div>}
            {error && <div className="alert alert-error">⚠️ {error}</div>}

            <div className="profile-sections">
                <form onSubmit={handleProfileSubmit} className="profile-card">
                    <h2>👤 {language === 'ar' ? 'معلومات الملف الشخصي' : 'Profile Information'}</h2>
                    
                    <div className="form-group">
                        <label>{t('emailAddress')}</label>
                        <input
                            type="email"
                            value={client?.email || ''}
                            disabled
                        />
                        <span className="helper-text">{language === 'ar' ? 'البريد الإلكتروني لا يمكن تغييره' : 'Email cannot be changed'}</span>
                    </div>

                    <div className="form-group">
                        <label>{t('fullName')} *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('phone')}</label>
                        <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder={language === 'ar' ? 'أدخل رقم هاتفك' : 'Enter your phone number'}
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('company')}</label>
                        <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            placeholder={language === 'ar' ? 'اسم شركتك' : 'Your company name'}
                        />
                    </div>

                    <button type="submit" className="save-btn" disabled={loading}>
                        {loading ? '⏳' : '✓'} {t('save')}
                    </button>
                </form>

                <form onSubmit={handlePasswordSubmit} className="profile-card">
                    <h2>🔒 {t('changePassword')}</h2>
                    
                    <div className="form-group">
                        <label>{t('currentPassword')}</label>
                        <input
                            type="password"
                            name="currentPassword"
                            value={passwordData.currentPassword}
                            onChange={handlePasswordChange}
                            placeholder="••••••••"
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('newPassword')}</label>
                        <input
                            type="password"
                            name="newPassword"
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            placeholder="••••••••"
                        />
                    </div>

                    <div className="form-group">
                        <label>{t('confirmPassword')}</label>
                        <input
                            type="password"
                            name="confirmPassword"
                            value={passwordData.confirmPassword}
                            onChange={handlePasswordChange}
                            placeholder="••••••••"
                        />
                    </div>

                    <button type="submit" className="save-btn" disabled={passwordLoading}>
                        {passwordLoading ? '⏳' : '🔒'} {t('changePassword')}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default Profile;
